import PropTypes from 'prop-types';
import React, {Component} from 'react';
import ContentEditable from 'react-contenteditable';
import {zIndexTextEditBox} from 'crazyEditor/core/globals';
import _ from 'lodash';
import ReactDOM from 'react-dom';
import {pushScope, popScope} from 'shared/helpers/keymasterHelper';
import {
  isTextElement,
  getElementMethods,
  getElementDefinitionProps,
} from 'crazyEditor/core/elements/utils/elementDefinitionUtils';
import {setEditingTextElementId} from 'crazyEditor/redux/modules/stage/stage';
import {connect} from 'react-redux';
import * as MouseCatcherUtils from '../MouseCatcherUtils';

const KEY_SCOPE_NAME = 'editBoxKeyScope';

@connect(
  false,
  {setEditingTextElementId},
  false
)
export default class TextEditBox extends Component {
  constructor(props) {
    super(props);
    const {element} = this.props;

    this.initCkEditor = this.initCkEditor.bind(this);
    this.ckfinderEnabled = getElementDefinitionProps(element).ckfinderEnabled;
  }

  componentDidMount() {
    const {element} = this.props;

    pushScope(KEY_SCOPE_NAME);

    if (this.ckfinderEnabled) {
      this.initCkEditor(element.id);
    }
    _.defer(() => {
      const node = ReactDOM.findDOMNode(this.refs.editBox);
      if (node) {
        node.focus();
      }
    });

    document.addEventListener('mousedown', this.onMouseDown);
  }

  componentWillUnmount() {
    this.focusLocked = false;
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('mousedown', this.onMouseDown);
    if (this._editor) {
      this._editor.destroy();
    }
    popScope();
    this.debouncedUpdateElement.cancel();
    if (this.changed) {
      this.updateElement();
    }
  }

  onKeyDown = event => {
    if (event.keyCode === 27) {
      const {setEditingTextElementId} = this.props;
      setEditingTextElementId(null);
    }
  };

  onMouseDown = event => {
    if (event.stopPropagation) {
      if (this.isChildElement(event.target)) {
        this.focusLocked = true;
        event.stopPropagation();
        document.addEventListener('mouseup', this.handleMouseUp, {once: true});
      }
    } else {
      if (MouseCatcherUtils.isCkeditorToolbar(event.target)) {
        this.focusLocked = true;
        document.addEventListener('mouseup', this.handleMouseUp, {once: true});
      }
    }
  };

  onChange = () => {
    this.changed = true;
    this.debouncedUpdateElement();
  };

  getEditBoxId = () => {
    return `${this.props.element.id}-text-box-container`;
  };

  isChildElement(element) {
    let nextElement = element;
    const editBoxId = this.getEditBoxId();
    while (nextElement) {
      if (nextElement.id === editBoxId) {
        return true;
      }
      nextElement = nextElement.parentElement;
    }
    return false;
  }

  updateElement = () => {
    const {onChange} = this.props;
    const htmlElementRef = this.refs.editBox.htmlEl;
    onChange(htmlElementRef.innerHTML);
    this.changed = false;
  };

  debouncedUpdateElement = _.debounce(this.updateElement, 200);

  initCkEditor() {
    this._editor = window.CKEDITOR.inline(this.getEditBoxId());
    this._editor.on('change', this.debouncedUpdateElement);
  }

  handleMouseUp = event => {
    _.defer(() => (this.focusLocked = false));
  };

  handlePaste = event => {
    if (!this.ckfinderEnabled) {
      _.defer(() => {
        const htmlElementRef = this.refs.editBox.htmlEl;
        const allInnerElements = htmlElementRef.getElementsByTagName('*');
        const elementsToClean = [];
        _.forEach(allInnerElements, innerElement => {
          if (innerElement) {
            switch (innerElement.tagName) {
              case 'DIV':
              case 'BR':
              case 'P':
                innerElement.removeAttribute('style');
                break;
              default:
                elementsToClean.push(innerElement);
                break;
            }
          }
        });
        elementsToClean.forEach(element => {
          element.parentNode.replaceChild(document.createTextNode(element.textContent), element);
        });
      });
    }
  };

  render() {
    const {element} = this.props;
    const elementMethods = getElementMethods(element.type);
    let elementEditBoxStyles;
    if (element.parent) {
      const parentElementMethods = getElementMethods(element.parent.type);
      elementEditBoxStyles = parentElementMethods.getEditBoxStyles(element.parent, element);
    } else {
      elementEditBoxStyles = elementMethods.getEditBoxStyles(element);
    }
    const style = {
      ...elementEditBoxStyles,
      width: '100%',
      boxSizing: 'border-box',
      cursor: 'text',
      flexGrow: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    };
    return (
      <ContentEditable
        {..._.omit(this.props, ['data', 'onChange'])}
        id={this.getEditBoxId()}
        html={elementMethods.getText(element)}
        style={style}
        className={elementMethods.getEditBoxClassName && elementMethods.getEditBoxClassName()}
        onMouseDown={this.onMouseDown}
        onClick={event => {
          event.stopPropagation();
        }}
        onChange={!this.ckfinderEnabled && this.debouncedUpdateElement}
        onMouseUp={event => {
          event.stopPropagation();
          if (this.focusLocked && this.isChildElement(event.target)) {
            event.nativeEvent.stopImmediatePropagation();
          }
          this.focusLocked = false;
        }}
        onPaste={this.handlePaste}
        ref="editBox"
        onKeyDown={this.onKeyDown}
      />
    );
  }
}

TextEditBox.propTypes = {
  element: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};
